// frontend/src/GiftCards/NewJob.jsx — /gift-cards/new
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Checkbox, Combobox, FormLayout, Icon, Listbox, TextField } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { Confirm, Modal } from '../KlaviyoProfileCleanup/common';
import { gcApi } from './apiClient';
import { Alert, Pill, money, useList } from './common';

const CODE_LENGTH = 32;
const example = (prefix) => prefix + '4821390776552139608841755219'.slice(0, Math.max(0, CODE_LENGTH - prefix.length));

const MODES = [['prefix', 'Prefix + random digits'], ['exact', 'One exact code'], ['mine', 'Use my codes']];

// The review window for your own codes: every row with its code, value and what is wrong with it. The job starts only from here.
function CheckCodes({ result, busy, error, onBack, onCreate }) {
  const { rows, summary } = result;
  return (
    <Modal title={`Check ${summary.rows} code${summary.rows === 1 ? '' : 's'}`} wide onClose={onBack} footer={<>
      <button type="button" className="uh-btn" onClick={onBack}>Back to the list</button>
      <button type="button" className="uh-btn primary" disabled={busy || summary.problems > 0} onClick={onCreate}>Create {summary.ready} gift card{summary.ready === 1 ? '' : 's'}</button></>}>
      <Alert>{error}</Alert>
      <div className="uh-gc-strip">
        {[[summary.rows, 'Rows'], [summary.ready, 'Ready'], [summary.problems, 'Need a fix'], [money(summary.total), 'Total value']].map(([v, k]) => (
          <div key={k}><div className="v">{typeof v === 'number' ? v.toLocaleString() : v}</div><div className="k">{k}</div></div>))}
      </div>
      {summary.problems > 0
        ? <div className="uh-kpc-banner warn">{summary.problems} row{summary.problems === 1 ? ' needs' : 's need'} a fix. Change the list and check again; nothing is created until every row is ready.</div>
        : <div className="uh-kpc-banner ok">Every code is ready: {summary.ready} card{summary.ready === 1 ? '' : 's'}, {money(summary.total)} in all.</div>}
      <div className="uh-tscroll"><table className="uh-tbl">
        <thead><tr><th className="r">Line</th><th>Code</th><th className="r">Value</th><th>Status</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.line} className={r.problem ? 'uh-gc-bad' : ''}>
            <td className="r uh-num">{r.line}</td>
            <td><span className="uh-mono">{r.code || <span className="uh-faint">(empty)</span>}</span>{r.raw && r.raw !== r.code && <div className="uh-faint" style={{ fontSize: 12 }}>as typed: {r.raw}</div>}</td>
            <td className="r uh-num">{r.amount == null ? <span className="uh-faint">—</span> : money(r.amount)}</td>
            <td>{r.problem ? <Pill tone="can">{r.problem}</Pill> : <Pill tone="ful">Ready</Pill>}</td>
          </tr>))}</tbody>
      </table></div>
      <div className="uh-muted" style={{ fontSize: 13 }}>Checked against Lightspeed and every earlier gift card job. Each card is created and loaded in Lightspeed, about 1.5 seconds per card.</div>
    </Modal>
  );
}

export default function NewJob() {
  const navigate = useNavigate();
  const [f, setF] = useState({ count: '1', prefix: '', amount: '', purpose: '', note: '', forClub: false, club: '', billable: false, custom_code: '' });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const { data: clubData } = useList(() => gcApi.clubs(), []);
  const clubs = useMemo(() => (clubData && clubData.clubs) || [], [clubData]);
  const [clubSearch, setClubSearch] = useState('');
  const matches = useMemo(() => { const q = clubSearch.trim().toLowerCase(); return (q ? clubs.filter((c) => c.toLowerCase().includes(q)) : clubs).slice(0, 200); }, [clubs, clubSearch]);
  const [mode, setMode] = useState('prefix');
  const [codesText, setCodesText] = useState('');
  const [checked, setChecked] = useState(null);        // the review window's rows, or null when it is closed
  const [checking, setChecking] = useState(false);
  const fileRef = useRef(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const count = Math.max(0, parseInt(f.count, 10) || 0);
  const amount = Number(String(f.amount).replace(/[$,]/g, '')) || 0;
  const mine = mode === 'mine';
  const single = mode === 'exact';
  const useCustom = single;
  const prefixOk = /^[A-Za-z0-9]{1,20}$/.test(f.prefix);
  const ready = mine
    ? codesText.trim() !== '' && f.purpose.trim() && (!f.forClub || f.club)
    : (useCustom || (count >= 1 && count <= 1000)) && amount > 0 && f.purpose.trim() && (useCustom ? /^[A-Za-z0-9]{1,32}$/.test(f.custom_code.trim()) : prefixOk) && (!f.forClub || f.club);
  const check = async () => {
    setChecking(true); setErr('');
    try { setChecked(await gcApi.checkCodes({ codes_text: codesText, amount: f.amount })); } catch (e) { setErr(e.message); } finally { setChecking(false); }
  };
  const readFile = (file) => { if (!file) return; const r = new FileReader(); r.onload = () => setCodesText(String(r.result || '')); r.readAsText(file); };
  const summary = `${count} card${count === 1 ? '' : 's'} × ${money(amount)} = ${money(count * amount)}`;
  const sending = useRef(false);
  const submit = async () => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true); setErr('');
    try {
      const common = { purpose: f.purpose, note: f.note, club: f.forClub ? f.club : null, billable_to_club: f.forClub && f.billable };
      const r = mine
        ? await gcApi.create({ ...common, prefix: null, custom_code: null, amount: f.amount, codes_text: codesText })
        : await gcApi.create({ ...common, count: useCustom ? 1 : count, prefix: useCustom ? null : f.prefix, custom_code: useCustom ? f.custom_code.trim() : null, amount });
      navigate(`/gift-cards/jobs/${r.id}`);
    } catch (e) { setErr(e.message); setConfirm(false); } finally { sending.current = false; }
    setBusy(false);
  };
  return (
    <section className="uh-page">
      <div className="uh-ph"><div><h1 className="uh-h1">New gift card job</h1><div className="uh-sub">Nothing reaches Lightspeed until you confirm the summary.</div></div></div>
      <div className="uh-card"><div className="uh-card-b">
        <Alert>{err}</Alert>
        <div className="uh-gc-form">
          <FormLayout>
            <div className="uh-gc-modes" role="radiogroup" aria-label="Codes">
              <span className="lab">Codes</span>
              <div className="uh-gc-seg">{MODES.map(([id, label]) => <button key={id} type="button" role="radio" aria-checked={mode === id} onClick={() => setMode(id)}>{label}</button>)}</div>
            </div>
            {mine && (
              <div className="uh-gc-codes" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); readFile(e.dataTransfer.files[0]); }}>
                <TextField label="Paste codes, or drop a CSV" value={codesText} onChange={setCodesText} multiline={6} autoComplete="off" monospaced
                  placeholder={'One code per line, or code,value\nSPFALL2026A0001,25\nSPFALL2026A0002,50'}
                  helpText="One column: every code gets the Amount below. Two columns: code, value. A header row is fine. Spaces and dashes are taken out; letters and digits, up to 32." />
                <div><button type="button" className="uh-btn slim" onClick={() => fileRef.current && fileRef.current.click()}>Choose a CSV file</button>
                  <input ref={fileRef} type="file" accept=".csv,.txt,text/csv,text/plain" hidden onChange={(e) => { readFile(e.target.files[0]); e.target.value = ''; }} /></div>
              </div>
            )}
            <FormLayout.Group>
              {mode === 'prefix' && <TextField label="Number of gift cards" type="number" min={1} max={1000} value={f.count} onChange={set('count')} autoComplete="off" requiredIndicator />}
              <TextField label="Amount" type="text" prefix="$" value={f.amount} onChange={set('amount')} autoComplete="off" requiredIndicator={!mine} helpText={mine ? 'Per card, for any code that has no value of its own' : 'Per card'} />
            </FormLayout.Group>
            {mode === 'prefix' && <TextField label="Prefix" value={f.prefix} onChange={set('prefix')} autoComplete="off" requiredIndicator maxLength={20}
              helpText="Letters and digits, up to 20. Kept exactly as typed; random digits fill the rest of the 32 characters." error={f.prefix && !prefixOk ? 'Letters and digits only, up to 20' : undefined} />}
            {single && <TextField label="Code" value={f.custom_code} onChange={set('custom_code')} autoComplete="off" maxLength={32} requiredIndicator helpText="One card. Letters and digits, up to 32." />}
            <TextField label="Purpose" value={f.purpose} onChange={set('purpose')} autoComplete="off" requiredIndicator helpText="Why these cards exist, e.g. Spring registration prizes. Shown on the job and in Lightspeed's account name." />
            <Checkbox label="Is this for a club?" checked={f.forClub} onChange={set('forClub')} />
            {f.forClub && (
              <>
                <Combobox activator={<Combobox.TextField prefix={<Icon source={SearchIcon} />} label="Club" placeholder="Start typing a club…" value={clubSearch || f.club}
                  onChange={(v) => { setClubSearch(v); if (f.club && v !== f.club) set('club')(''); }} onFocus={() => { if (clubSearch === f.club) setClubSearch(''); }} autoComplete="off" requiredIndicator />}>
                  {matches.length > 0 ? (
                    <Listbox onSelect={(v) => { set('club')(v); setClubSearch(v); }}>
                      {matches.map((c) => <Listbox.Option key={c} value={c} selected={f.club === c} accessibilityLabel={c}>{c}</Listbox.Option>)}
                      {clubs.length > matches.length && <Listbox.Option value="__more__" disabled accessibilityLabel="More">Keep typing to narrow the list…</Listbox.Option>}
                    </Listbox>) : null}
                </Combobox>
                <Checkbox label="Billable to the club" checked={f.billable} onChange={set('billable')} disabled={!f.club} />
              </>
            )}
            <TextField label="Note" value={f.note} onChange={set('note')} autoComplete="off" multiline={2} helpText="Kept with the job. Not printed." />
          </FormLayout>
          {!mine && <div className="uh-gc-preview">{useCustom ? <>Code: <span className="uh-mono">{f.custom_code.trim()}</span> · 1 card × {money(amount)}</> : <>Example code: <span className="uh-mono">{prefixOk ? example(f.prefix) : '…'}</span> · {summary}</>}</div>}
          <div>{mine
            ? <button type="button" className="uh-btn primary" disabled={!ready || checking} onClick={check}>{checking ? 'Checking…' : 'Check codes'}</button>
            : <button type="button" className="uh-btn primary" disabled={!ready || busy} onClick={() => setConfirm(true)}>Create gift cards</button>}{' '}
            <button type="button" className="uh-btn" onClick={() => navigate('/gift-cards')}>Cancel</button></div>
        </div>
      </div></div>
      {checked && <CheckCodes result={checked} busy={busy} error={err} onBack={() => { setChecked(null); setErr(''); }} onCreate={submit} />}
      {confirm && <Confirm title="Create these gift cards in Lightspeed?" yes="Create" busy={busy} error={err}
        text={`${useCustom ? `1 card × ${money(amount)}` : summary}${f.forClub && f.club ? ` for ${f.club}${f.billable ? ' (billable to the club)' : ''}` : ''}. Purpose: ${f.purpose.trim()}. Each card is created and loaded in Lightspeed; this takes about 1.5 seconds per card.`}
        onYes={submit} onNo={() => setConfirm(false)} />}
    </section>
  );
}
